import { useEffect, useRef } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { relationshipLabels, selectOptions } from "@/lib/benefit-enums"
import { formatPersonName } from "@/lib/person-identity"
import { useAppDispatch, useAppSelector } from "@/store"
import { staffSessionActions } from "@/store/staffSessionSlice"
import { benefitApi } from "@/features/benefit-workspace/benefitApi"
import { benefitCreationActions as actions, copyConfirmation, creationLocked, type HouseholdCopyConfirmation } from "@/features/benefit-workspace/benefitCreationSlice"
export type { HouseholdCopyConfirmation } from "@/features/benefit-workspace/benefitCreationSlice"

/** Query owns saved source; Redux owns only the captured roster/token and relationship answers. */
export function HouseholdCopyDialog({ sourceId, applicantId, busy, onClose, onConfirm, onReturnFocus }: {
  sourceId: string | null
  applicantId: string
  busy: boolean
  onClose: () => void
  onReturnFocus?: () => void
  onConfirm: (sourceId: string, confirmation: HouseholdCopyConfirmation) => Promise<boolean>
}) {
  const dispatch = useAppDispatch()
  const opener = useRef<HTMLElement | null>(null)
  const state = useAppSelector(s => s.benefitCreation)
  const copy = state.copy?.sourceId === sourceId && state.copy.applicantId === applicantId ? state.copy : null
  const locked = busy || creationLocked(state)
  const read = benefitApi.useGetBenefitCopySourceQuery(state.scopeKey && sourceId
    ? { scopeKey: state.scopeKey, benefitId: sourceId } : skipToken, { refetchOnMountOrArgChange: true })
  const source = read.currentData
  const valid = !!source?.concurrencyToken && source.personCollection.some(p => p.id === applicantId)
    && new Set(source.personCollection.map(p => p.id)).size === source.personCollection.length
  useEffect(() => {
    if (read.error && "status" in read.error && [401, 403, 404].includes(Number(read.error.status))) {
      dispatch(staffSessionActions.scopeRevoked())
    } else if (copy && !copy.baseline && !read.isFetching && !read.isError && valid && source) {
      dispatch(actions.copyLoaded({ id: copy.id, baseline: { token: source.concurrencyToken!, members: source.personCollection.map(p => ({
        id: p.id, firstName: p.firstName, lastName: p.lastName, ...(p.middleName ? { middleName: p.middleName } : {}),
      })) } }))
    }
  }, [copy, source, valid, read.isFetching, read.isError, read.error, dispatch])
  const confirmation = copyConfirmation(copy)
  const applicant = copy?.baseline?.members.find(p => p.id === applicantId)
  const close = () => { if (!locked) { dispatch(actions.copyClosed()); onClose() } }
  const confirm = async () => {
    if (!sourceId || !confirmation || locked) return
    if (await onConfirm(sourceId, confirmation)) close()
  }
  return <Dialog open={sourceId !== null && copy !== null} onOpenChange={open => { if (!open) close() }}>
    <DialogContent onOpenAutoFocus={() => {
      const active = document.activeElement
      if (active instanceof HTMLElement && !active.closest('[role="dialog"]')) opener.current = active
    }} onCloseAutoFocus={event => {
      if (onReturnFocus) { event.preventDefault(); onReturnFocus() }
      else if (opener.current?.isConnected) { event.preventDefault(); opener.current.focus() }
    }}>
      <DialogHeader>
        <DialogTitle>Confirm household relationships</DialogTitle>
        <DialogDescription>
          {applicant ? `${formatPersonName(applicant)} will be the new applicant. ` : "The selected household member will be the new applicant. "}
          Choose how each person is related to this applicant. Previous relationships cannot be assumed.
          The original file is unchanged; income, housing answers, grants and payments are not copied.
        </DialogDescription>
      </DialogHeader>
      {copy?.baseline ? <div className="max-h-[50vh] space-y-4 overflow-y-auto">
        {copy.baseline.members.map(person => <div key={person.id} className="space-y-1">
          <Label htmlFor={`copy-relationship-${person.id}`}>{formatPersonName(person)}</Label>
          {person.id === applicantId ? <p className="text-sm">Applicant (Self)</p> : <Select value={copy.relationships[person.id] ?? ""} disabled={locked}
            onValueChange={value => dispatch(actions.relationshipChanged({ id: copy.id, personId: person.id, value }))}>
            <SelectTrigger id={`copy-relationship-${person.id}`}><SelectValue placeholder="Relationship to new applicant" /></SelectTrigger>
            <SelectContent>{selectOptions(relationshipLabels).filter(option => option.value !== "0").map(option =>
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
          </Select>}
        </div>)}
      </div> : read.isError ? <p role="alert">Could not load the source household. <Button variant="outline" onClick={() => void read.refetch()}>Retry source read</Button></p>
        : !read.isFetching && source && !valid ? <p role="alert">The applicant or source household changed. Close this dialog and reload the application list.</p>
          : <p>Loading household…</p>}
      {state.message && <p role="alert">{state.message}</p>}
      {state.attempt?.phase === "conflict" && <Button variant="outline" onClick={() => {
        if (window.confirm("Discard these relationship answers and the original source token? Reopen the source to review fresh data.")) dispatch(actions.discardConfirmed())
      }}>Discard conflicting confirmation</Button>}
      {state.attempt?.phase === "outcomeUnknown" && <Button variant="outline" onClick={() => dispatch(actions.unknownExitAcknowledged())}>Acknowledge unknown outcome and leave read-only</Button>}
      <DialogFooter>
        <Button variant="outline" disabled={locked} onClick={close}>Cancel</Button>
        <Button disabled={!confirmation || locked} onClick={() => void confirm()}>{state.attempt?.phase === "saving" ? "Creating…" : "Create with this household"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
}
